import {MicromorgiIconMotion} from '@/assets/icons/icons'
import {Modal} from '@/components/ui/modal/Modal'
import {useMe} from '@/features/authentication/queries/useMe'
import useMixPanel from '@/integrations/mixpanel/hooks/useMixpanel'
import {MixpanelEventNameE} from '@/integrations/mixpanel/types'
import {useRootStore} from '@/store'
import {selectConfirmSendGiftModal, selectPaidGifsModal} from '@/store/selectors'
import {AnimatePresence, motion} from 'framer-motion'
import {FC, useCallback} from 'react'
import {useTranslation} from 'react-i18next'
import {PulseAnimation} from '../animations/pulse-animation/PulseAnimation'
import {useGetCurrentPaidGif} from '../queries/useGetCurrentPaidGif'
import {useSendMicromorgi} from '../queries/useSendMicromorgi'
import {
    StyledAmount,
    StyledAnimation,
    StyledConfirmButton,
    StyledConfirmSendGiftModalWrapper,
    StyledConfirmText,
    StyledPulseWrapper
} from './styles'
import {useIsMutating} from '@tanstack/react-query'
import {TypeOfConfirmModalE} from './store/createConfirmSendGiftModalSlice'
import {
    MUTATION_KEYS as UNLOCK_TEXT_MUTATION_KEYS,
    QUERY_KEYS as UNLOCK_PAID_MEDIA_QUERY_KEYS
} from '@/features/chat/queries/keys.ts'
import {useGetCreatorProfile} from '@/features/guest/queries/useGetCreatorProfile'
import {useDonateGoalV3} from '@/features/goal/queries/useDonateGoalV3'

const transitionDuration = 1.5
const modalVariants = {
    initial: {
        opacity: 0,
        x: '-50%',
        y: '-40%'
    },
    animate: {
        x: '-50%',
        y: '-50%',
        opacity: 1,
        transition: {duration: transitionDuration}
    }
}

export const ConfirmSendGiftModal: FC = () => {
    const {t} = useTranslation()
    const {closeModal: closeGifsModal} = useRootStore(selectPaidGifsModal)
    const {
        confirmText,
        onConfirmCallBack,
        amount,
        animationID = 0,
        hostID,
        goalId,
        closeModal,
        setStatus,
        typeOfModal
    } = useRootStore(selectConfirmSendGiftModal)

    const onSendMicromoriSuccess = () => {
        closeModal()
        closeGifsModal()
    }

    const mixpanel = useMixPanel()
    const {mutateAsync: sendMicromorgi, isPending: isPendingSendMicromorgi} = useSendMicromorgi(onSendMicromoriSuccess)
    const {mutateAsync: donateGoal, isPending: isPendingDonateGoal} = useDonateGoalV3(status => {
        setStatus(status)
        closeModal()
    })
    const {data: animationIcon} = useGetCurrentPaidGif({animation_id: animationID})
    const {data: user} = useMe()
    const {data: host} = useGetCreatorProfile({creatorIdentifier: hostID})

    const isPendingUnlockTextMessage = useIsMutating({mutationKey: [UNLOCK_TEXT_MUTATION_KEYS.unlockTextMessage]})
    const isPendingUnlockMediaMessage = useIsMutating({
        mutationKey: [UNLOCK_PAID_MEDIA_QUERY_KEYS.getUnlockedMediaMessage]
    })

    const onClickSend = useCallback(async () => {
        if (amount && hostID && user) {
            if (goalId) {
                await donateGoal({creatorId: hostID, goalId, micromorgiAmount: amount})
            } else {
                await sendMicromorgi({
                    micromorgiAmount: amount,
                    creatorId: hostID,
                    animationId: animationID
                })

                if (animationID) {
                    mixpanel?.trackEvent(MixpanelEventNameE.AnimationSend, {
                        animation: animationID,
                        morgi_amount: amount,
                        guest_id: user.id
                    })
                }
            }
            closeModal()
        }
    }, [amount, hostID, closeModal, donateGoal, sendMicromorgi, user, goalId])

    return (
        <Modal onClose={closeModal} width={'300px'} animation={modalVariants} id="id-of-confirm-modal">
            {animationID && typeOfModal === TypeOfConfirmModalE.giftList ? (
                <StyledAnimation src={animationIcon?.url} />
            ) : (
                <StyledPulseWrapper>
                    <PulseAnimation duration={transitionDuration} />
                    {typeOfModal !== TypeOfConfirmModalE.giftList && (
                        <StaticMotionPulseMicromorgiIcon iconSize={{height: 82, width: 59}} />
                    )}
                </StyledPulseWrapper>
            )}

            <StyledConfirmSendGiftModalWrapper>
                {amount && <StyledAmount>{`+${amount}`}</StyledAmount>}
                <StyledConfirmText>
                    {confirmText ??
                        t('chat:send_user_micromorgi?', {
                            USER: host?.fullName
                        })}
                </StyledConfirmText>
                <StyledConfirmButton
                    disabled={
                        isPendingSendMicromorgi ||
                        isPendingDonateGoal ||
                        !!isPendingUnlockTextMessage ||
                        !!isPendingUnlockMediaMessage
                    }
                    variant="secondary"
                    onClick={typeof onConfirmCallBack === 'function' ? onConfirmCallBack : onClickSend}
                >
                    {t('common:confirm')}
                </StyledConfirmButton>
            </StyledConfirmSendGiftModalWrapper>
        </Modal>
    )
}

const defaultWidth = 59
const defaultHeight = 82

type StaticMotionPulseMicromorgiIconProps = {
    iconSize: {width: number; height: number}
}
const StaticMotionPulseMicromorgiIcon: FC<StaticMotionPulseMicromorgiIconProps> = ({iconSize}) => {
    const getScale = (scaleValue: number) =>
        `${(defaultWidth / iconSize.width) * scaleValue}, ${(defaultHeight / iconSize.height) * scaleValue}`

    return (
        <AnimatePresence>
            <motion.div
                initial={{
                    position: 'absolute'
                }}
                animate={{
                    scale: [
                        getScale(1),
                        getScale(1),
                        getScale(0.8),
                        getScale(1),
                        getScale(0.8),
                        getScale(1),
                        getScale(0.8),
                        getScale(1)
                    ],
                    transition: {duration: 6},
                    pointerEvents: 'none',
                    ...iconSize
                }}
            >
                <MicromorgiIconMotion {...iconSize} />
            </motion.div>
        </AnimatePresence>
    )
}
